import styles from './styles.module.css'

import { MdCheck } from '@react-icons/all-files/md/MdCheck'
import { MdContentCopy } from '@react-icons/all-files/md/MdContentCopy'
import { MdInfo } from '@react-icons/all-files/md/MdInfo'
import clsx from 'clsx'
import React, { useCallback, useEffect, useState } from 'react'

import { sendMixpanelEvent } from '@disco/state-management/actions/AnalyticsActions'
import { getContrastColor } from '@disco/utils/color-utils'
import { pushUINotification } from '@disco/utils/notifications'

import {
  Box,
  Button as DsButton,
  Checkbox,
  Flex as DsFlex,
  IconButton,
  Text,
  TextField,
  Tooltip,
} from 'components/ds'

import { useDispatch } from 'hooks/use-redux'

const DEFAULT_WIDTH = 480
const DEFAULT_HEIGHT = 235
const MIN_WIDTH = 355

const THEMES = [
  { id: 'white', name: 'Light', color: '#fff' },
  { id: 'dark', name: 'Dark', color: '#333' },
]

const CONTROLS = [
  { id: '#4E98FF', color: '#4E98FF', name: 'Blue' },
  { id: '#3D5B01', color: '#3D5B01', name: 'Green' },
  { id: '#910460', color: '#910460', name: 'Magenta' },
  { id: '#D095FF', color: '#D095FF', name: 'Purple' },
]

const isValidHexColor = (hex: string) =>
  /^#([A-Fa-f0-9]{6}|[A-Fa-f0-9]{3})$/.test(hex)

const getFrameSrc = (url: string, params: Record<string, string | boolean>) => {
  if (!url) return url
  const src = new URL(url)
  Object.keys(params).forEach(key =>
    src.searchParams.append(key, String(params[key])),
  )
  return src.toString()
}

interface ColorSwatchButtonProps {
  color: string
  name: string
  selected: boolean
  onSelect: () => void
}

// A plain <button> rather than a styled RadioGroup.Item — fighting a native
// radio's own focus/checked pseudo-elements to turn it into a color swatch
// produced a square focus ring (CSS `outline` doesn't clip to border-radius)
// and a stray inner dot from Radix's own checked indicator. A button gives
// full control over its content (a real checkmark icon) and a focus ring
// that's just a circular box-shadow, which does respect border-radius.
const ColorSwatchButton: React.FC<ColorSwatchButtonProps> = ({
  color,
  name,
  selected,
  onSelect,
}) => (
  <button
    type="button"
    role="radio"
    aria-checked={selected}
    onClick={onSelect}
    className={styles.colorSwatchButton}
  >
    <span
      className={clsx(
        styles.colorSwatchCircle,
        selected && styles.colorSwatchCircleSelected,
      )}
      style={{ backgroundColor: color }}
    >
      {selected && <MdCheck size={14} color={getContrastColor(color)} />}
    </span>
    <Text size="2">{name}</Text>
  </button>
)

interface TrackEmbedTabProps {
  iframeId: string
  embedUrl: string
  embedParams: { download: boolean }
  onParamsChange: (params: Partial<{ download: boolean }>) => void
  onClose?: () => void
  disablePublicDownloads: boolean
}

/**
 * "Embed" panel. Builds the `<iframe>` embed code for a track with a live
 * preview, and lets the user customize player width, color theme/controls,
 * and whether downloads/artwork are enabled.
 */
export const TrackEmbedTab: React.FC<TrackEmbedTabProps> = ({
  iframeId,
  embedUrl,
  embedParams,
  onParamsChange,
  onClose,
  disablePublicDownloads,
}) => {
  const dispatch = useDispatch()

  useEffect(() => {
    dispatch(sendMixpanelEvent('Open embed tab (tracks)'))
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const [params, setParams] = useState({
    artwork: true,
    color: '#4E98FF',
    theme: 'white',
  })
  const [customColor, setCustomColor] = useState('#4E98FF')
  const [persistedCustomColor, setPersistedCustomColor] = useState('#FFFC52')
  const [isCustomColorActive, setCustomColorActive] = useState(false)
  // Kept as the raw string the user typed (not coerced to a number on every
  // keystroke) so clearing the field doesn't snap back to "0" and typing
  // doesn't get a phantom leading zero prefixed onto it.
  const [widthInput, setWidthInput] = useState(String(DEFAULT_WIDTH))
  const [isCustomizeOpen, setCustomizeOpen] = useState(false)

  const parsedWidth = Number(widthInput)
  const frameWidth =
    Number.isFinite(parsedWidth) && parsedWidth >= MIN_WIDTH
      ? parsedWidth
      : MIN_WIDTH
  const frameSrc = getFrameSrc(embedUrl, params)
  const frameCode = `<iframe id="${iframeId}" name="${iframeId}" allowfullscreen frameborder="0" class="disco-embed" src="${frameSrc}" width="${frameWidth}" height="${DEFAULT_HEIGHT}"></iframe>`

  const toggleCustomizeOpen = useCallback(() => {
    dispatch(sendMixpanelEvent('Embed tab - Customize player'))
    setCustomizeOpen(open => !open)
  }, [dispatch])

  const handleCopy = useCallback(async () => {
    try {
      await navigator.clipboard.writeText(frameCode)
      dispatch(sendMixpanelEvent('Embed tab - Copy embed code (tracks)'))
      pushUINotification({ level: 'success', message: 'Embed code copied!' })
    } catch {
      pushUINotification({
        level: 'error',
        message: 'Failed to copy embed code',
      })
    }
  }, [dispatch, frameCode])

  const handleControlColorChange = (value: string) => {
    if (value === 'custom') {
      setCustomColorActive(true)
      setCustomColor(persistedCustomColor)
      setParams(prev => ({ ...prev, color: persistedCustomColor }))
    } else {
      setCustomColorActive(false)
      setCustomColor(value)
      setParams(prev => ({ ...prev, color: value }))
    }
  }

  const handleCustomColorChange = (value: string) => {
    setCustomColor(value)
    if (isCustomColorActive && isValidHexColor(value)) {
      setParams(prev => ({ ...prev, color: value }))
      setPersistedCustomColor(value)
    }
  }

  return (
    <>
      <Box mb="3">
        <Text size="2" weight="medium" as="label" htmlFor="embed-code">
          Embed code
        </Text>
        <DsFlex gap="2" mt="1" align="center">
          <Box flexGrow="1">
            <TextField.Root
              id="embed-code"
              size="2"
              readOnly
              value={frameCode}
            />
          </Box>
          <Tooltip content="Copy">
            <IconButton
              variant="soft"
              size="2"
              onClick={handleCopy}
              aria-label="Copy embed code"
            >
              <MdContentCopy />
            </IconButton>
          </Tooltip>
        </DsFlex>
      </Box>

      <Box mb="3">
        <Text size="2" weight="medium" as="div" mb="1">
          Player preview
        </Text>
        <Box p="3" className={styles.previewBox}>
          <iframe
            title="Embed preview"
            className={styles.embedPreviewFrame}
            src={frameSrc}
            height={DEFAULT_HEIGHT}
          />
        </Box>
      </Box>

      <DsFlex justify="between" align="center" mb="3">
        <DsButton
          variant="soft"
          color="gray"
          size="2"
          onClick={toggleCustomizeOpen}
        >
          {isCustomizeOpen ? 'Hide options' : 'Customize player'}
        </DsButton>
        <DsFlex gap="3">
          <DsButton variant="soft" color="gray" onClick={onClose}>
            Cancel
          </DsButton>
          <DsButton onClick={handleCopy}>Copy embed code</DsButton>
        </DsFlex>
      </DsFlex>

      {isCustomizeOpen && (
        <Box className={styles.scrollArea}>
          <Box mb="6">
            <DsFlex align="center" gap="1" mb="3">
              <Text size="2" weight="medium" as="div">
                Player Size
              </Text>
              <Tooltip content={`Player must be at least ${MIN_WIDTH}px wide`}>
                <IconButton
                  variant="ghost"
                  size="1"
                  radius="full"
                  aria-label="About player size"
                >
                  <MdInfo size={14} />
                </IconButton>
              </Tooltip>
            </DsFlex>
            <DsFlex gap="2" align="center">
              <TextField.Root
                type="number"
                size="2"
                value={widthInput}
                min={MIN_WIDTH}
                onChange={e => setWidthInput(e.target.value)}
                aria-label="Player width"
              />
              <Text size="2" color="gray">
                ×
              </Text>
              <TextField.Root
                type="number"
                size="2"
                value={DEFAULT_HEIGHT}
                readOnly
                aria-label="Player height"
              />
            </DsFlex>
          </Box>

          <Box mb="6">
            <Text size="2" weight="medium" as="div" mb="3">
              Color Theme
            </Text>
            <Box role="radiogroup" aria-label="Color Theme" pl="1">
              <DsFlex gap="4" align="center">
                {THEMES.map(theme => (
                  <ColorSwatchButton
                    key={theme.id}
                    color={theme.color}
                    name={theme.name}
                    selected={params.theme === theme.id}
                    onSelect={() =>
                      setParams(prev => ({ ...prev, theme: theme.id }))
                    }
                  />
                ))}
              </DsFlex>
            </Box>
          </Box>

          <Box mb="6">
            <Text size="2" weight="medium" as="div" mb="3">
              Control Colors
            </Text>
            <Box role="radiogroup" aria-label="Control Colors" pl="1">
              <DsFlex gap="4" wrap="wrap" mb="4">
                {CONTROLS.map(control => (
                  <ColorSwatchButton
                    key={control.id}
                    color={control.color}
                    name={control.name}
                    selected={
                      !isCustomColorActive && params.color === control.id
                    }
                    onSelect={() => handleControlColorChange(control.id)}
                  />
                ))}
              </DsFlex>
              <DsFlex align="center" gap="3">
                <ColorSwatchButton
                  color={
                    isValidHexColor(persistedCustomColor)
                      ? persistedCustomColor
                      : '#ffffff'
                  }
                  name="Custom"
                  selected={isCustomColorActive}
                  onSelect={() => handleControlColorChange('custom')}
                />
                {isCustomColorActive && (
                  <Box maxWidth="150px">
                    <TextField.Root
                      size="2"
                      value={customColor}
                      onChange={e => handleCustomColorChange(e.target.value)}
                      placeholder="#000000"
                      color={
                        customColor && !isValidHexColor(customColor)
                          ? 'red'
                          : undefined
                      }
                    />
                  </Box>
                )}
              </DsFlex>
            </Box>
          </Box>

          <DsFlex gap="4">
            <Text as="label" size="2">
              <DsFlex gap="2" align="center">
                <Checkbox
                  checked={embedParams.download}
                  disabled={disablePublicDownloads}
                  onCheckedChange={() =>
                    onParamsChange({ download: !embedParams.download })
                  }
                />
                Enable downloads
              </DsFlex>
            </Text>
            <Text as="label" size="2">
              <DsFlex gap="2" align="center">
                <Checkbox
                  checked={params.artwork}
                  onCheckedChange={() =>
                    setParams(prev => ({ ...prev, artwork: !prev.artwork }))
                  }
                />
                Enable artwork
              </DsFlex>
            </Text>
          </DsFlex>
        </Box>
      )}
    </>
  )
}
